class PredictiveSearch extends HTMLElement {
  constructor() {
    super();
    this.cachedMap = new Map();
    this.focusElement = this.input;
    this.resetButton.addEventListener('click', this.clear.bind(this));
    this.input.addEventListener('input', FoxTheme.utils.debounce(this.onChange.bind(this), 300));
    this.input.addEventListener('focus', this.onFocus.bind(this));
    this.querySelector('form').addEventListener('submit', this.handleFormSubmit.bind(this));
    
    this.searchContent = this.querySelector('.search__content');
    this.searchRecommendationEmpty = this.dataset.searchRecommendationEmpty === 'true';
    this.header = document.querySelector('header');
    this.toggleNavigationButton = document.querySelector('.toggle-navigation-button');

    this.boundHandleClickOutside = this.handleClickOutside.bind(this);
    document.addEventListener('click', this.boundHandleClickOutside);
    this.searchProductTypes?.addEventListener('change', this.handleProductTypeChange.bind(this));

    this.isHeaderSearch = this.closest('header') !== null;

    document.addEventListener('menu-drawer:open', () => {
      this.classList.remove('predictive-search-open');
      document.body.classList.remove('search-open');
      const el = document.querySelector('.header__search--wrap');
      if(el) {
        el.classList.add('hidden');
      }
    });

    this.states = {
      OPEN: 'predictive-search-open',
      LOADING: 'btn--loading',
      SEARCH_OPEN: 'search-open',
    };
  }

  get input() {
    return this.querySelector('input[type="search"]');
  }
  get resetButton() {
    return this.querySelector('button[type="reset"]');
  }

  get searchProductTypes() {
    return this.querySelector('#SearchProductTypes');
  }

  onFocus(event) {
    document.documentElement.style.setProperty('--viewport-height', `${window.innerHeight}px`);
    document.documentElement.style.setProperty(
      '--header-bottom-position',
      `${parseInt(this.header.getBoundingClientRect().bottom)}px`
    );
    if (this.isHeaderSearch && !this.searchRecommendationEmpty) {
      document.body.classList.add('search-open');
      if (this.toggleNavigationButton) {
        this.toggleNavigationButton.classList.remove('is-show');
      }
    }

    if (!this.searchRecommendationEmpty) {
      this.searchContent.classList.remove('hidden');
    }
    this.classList.add('predictive-search-open');
    if (this.getQuery().length === 0) {
      if (this.searchRecommendationEmpty) {
        this.searchContent.classList.add('hidden');
      }
      return;
    }
    const url = this.setupURL().toString();
    this.renderSection(url, event);
  }

  getQuery() {
    return this.input.value.trim();
  }

  clear(event = null) {
    event?.preventDefault();
    this.input.value = '';
    this.input.focus();
    this.removeAttribute('results');
    this.searchContent?.classList.add('hidden');
  }

  handleFormSubmit(event) {
    if (this.getQuery().length === 0) {
      event.preventDefault();
      return false;
    }
  }

  handleProductTypeChange(evt) {
    const query = this.getQuery();
    if (query.length > 0) {
      const url = this.setupURL().toString();
      this.renderSection(url);
    }
  }

  setupURL() {
    const url = new URL(`${FoxTheme.routes.shop_url}${FoxTheme.routes.predictive_search_url}`);
    let search_term = this.getQuery();
    if (this.searchProductTypes && this.searchProductTypes.value != '') {
      search_term = `product_type:${this.searchProductTypes.value} AND ${encodeURIComponent(search_term)}`;
    }
    return (
      url.searchParams.set('q', search_term),
      url.searchParams.set('resources[limit]', this.dataset.resultsLimit || 3),
      url.searchParams.set('resources[limit_scope]', 'each'),
      url.searchParams.set('section_id', FoxTheme.utils.getSectionId(this)),
      url
    );
  }

  onChange() {
    if (this.getQuery().length === 0) {
      this.clear();
      return;
    }
    const url = this.setupURL().toString();
    this.renderSection(url);
  }

  renderSection(url) {
    this.cachedMap.has(url) ? this.renderSectionFromCache(url) : this.renderSectionFromFetch(url);
  }

  renderSectionFromCache(url) {
    const responseText = this.cachedMap.get(url);
    this.renderSearchResults(responseText), this.setAttribute('results', '');
  }

  renderSectionFromFetch(url) {
    this.setLoadingState(true);

    fetch(url)
      .then((response) => {
        if (!response.ok) throw new Error('Network response was not ok');
        return response.text();
      })
      .then((responseText) => {
        this.renderSearchResults(responseText);
        this.cachedMap.set(url, responseText);
      })
      .catch((error) => {
        console.error('Error fetching data: ', error);
        this.setAttribute('error', 'Failed to load data');
      })
      .finally(() => this.setLoadingState(false));
  }
  renderSearchResults(responseText) {
    const id = 'PredictiveSearchResults-' + FoxTheme.utils.getSectionId(this);
    const targetElement = document.getElementById(id);

    if (targetElement) {
      const parser = new DOMParser();
      const parsedDoc = parser.parseFromString(responseText, 'text/html');
      const contentElement = parsedDoc.getElementById(id);

      if (contentElement) {
        this.searchContent?.classList.remove('hidden');
        targetElement.innerHTML = contentElement.innerHTML;

        // Update the counter with real total count immediately
        this.updateSearchCounterFast();

        if (this.isHeaderSearch && !document.body.classList.contains('search-open')) {
          document.body.classList.add('search-open');
          this.toggleNavigationButton.classList.add('is-show');
          this.classList.add('predictive-search-open');
        } else {
          this.classList.add('predictive-search-open');
        }
      } else {
        console.error(`Element with id '${id}' not found in the parsed response.`);
      }
    } else {
      console.error(`Element with id '${id}' not found in the document.`);
    }
  }

  updateSearchCounterFast() {
    const counterElement = this.querySelector('.search-results-counter p');
    if (!counterElement) return;

    const query = this.getQuery();
    if (!query) return;

    const isEditorMode = window.Shopify?.designMode || 
                        document.body.classList.contains('shopify-design-mode') ||
                        window.location.search.includes('preview_theme_id');

    counterElement.textContent = FoxTheme.strings.loading;

    if (isEditorMode) {
      this.updateCounterFromPredictiveResults(counterElement);
      return;
    }

    const searchUrl = new URL(`${FoxTheme.routes.shop_url}/search`);
    searchUrl.searchParams.set('q', query);
    searchUrl.searchParams.set('type', 'product');

    fetch(searchUrl)
      .then(response => response.text())
      .then(html => {
        const parser = new DOMParser();
        const doc = parser.parseFromString(html, 'text/html');
        
        let totalCount = 0;
        
        const selectors = [
          '.card-product',
          '.product-card', 
          '.product-item',
          '[data-product-id]',
          '.product-grid .product',
          '.collection-product-grid .product'
        ];
        
        for (const selector of selectors) {
          const elements = doc.querySelectorAll(selector);
          if (elements.length > 0) {
            totalCount = elements.length;
            break;
          }
        }
        
        if (!totalCount) {
          const paginationText = doc.querySelector('.pagination__info, .collection-product-count, .search-results-count');
          if (paginationText) {
            const text = paginationText.textContent;
            const match = text.match(/(\d+)/);
            if (match) {
              totalCount = parseInt(match[1]);
            }
          }
        }
        
        if (!totalCount) {
          const metaCount = doc.querySelector('meta[name="product-count"], meta[property="product:count"]');
          if (metaCount) {
            totalCount = parseInt(metaCount.getAttribute('content'));
          }
        }
      
        const shownCount = this.querySelectorAll('.product-card').length;
        
        if (totalCount > 0) {
          const previewShowingKey = FoxTheme.strings.search_preview_showing;
          
          const newText = previewShowingKey
            .replace('{{ total }}', totalCount)
            .replace('{{ count }}', shownCount);
          
          counterElement.textContent = newText;
        } else {
          this.updateCounterFromPredictiveResults(counterElement);
        }
      })
      .catch(error => {
        console.error('Error fetching total count:', error);
        this.updateCounterFromPredictiveResults(counterElement);
      });
  }

  updateCounterFromPredictiveResults(counterElement) {
    const predictiveResults = this.querySelectorAll('.product-card, .card-product, .product-item');
    const predictiveCount = predictiveResults.length;
    const shownCount = this.querySelectorAll('.product-card').length;
    
    if (predictiveCount > 0) {
      const previewShowingKey = FoxTheme.strings.search_preview_showing;
      const newText = previewShowingKey
        .replace('{{ total }}', predictiveCount)
        .replace('{{ count }}', shownCount);
      counterElement.textContent = newText;
    } else {
      counterElement.textContent = FoxTheme.strings.loading;
    }
  }

  handleClickOutside(event) {
    const target = event.target;
    
    // Check if click is on the predictive-search element or its children
    const isPredictiveSearchClick = target.closest('predictive-search');
    
    // Check if click is on the header search trigger button
    const isHeaderSearchClick = target.closest('.header--search');
    
    // Don't close if clicking on predictive-search element or header search button
    if (isPredictiveSearchClick || isHeaderSearchClick) {
      return;
    }
    
    // Close the search for any other click
    const shouldClose = this.isHeaderSearch
      ? !this.contains(target)
      : !this.contains(target);

    if (shouldClose) {
      setTimeout(() => this.toggleSearchState(false));
    }
  }

  toggleSearchState(isOpen) {
    this.classList.toggle(this.states.OPEN, isOpen);
    const el = document.querySelector('.header__search--wrap');
    const elSearch = document.querySelector('.header--search');
    if(el) {
      el.classList.add('hidden');
    }
    if(elSearch){
      elSearch.classList.remove('hidden');
    }
    if (this.isHeaderSearch) {
      document.body.classList.toggle(this.states.SEARCH_OPEN, isOpen);
      this.toggleNavigationButton.classList.toggle('is-show', !isOpen);
    }
    if (!isOpen && this.searchRecommendationEmpty) {
      this.searchContent?.classList.add('hidden');
    }
    const search = document.querySelector('.header__search--wrap--desktop');
    if(search){
      search.classList.add('hidden');
    }
  }

  setLoadingState(isLoading) {
    if (isLoading) {
      this.setAttribute('loading', 'true');
      this.resetButton.classList.add(this.states.LOADING);
    } else {
      this.removeAttribute('loading');
      this.resetButton.classList.remove(this.states.LOADING);
      this.setAttribute('results', 'true');
    }
  }
}
customElements.define('predictive-search', PredictiveSearch);
